import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { normalizeStcTypeEmptyValue } from "../constants/scheduleDraftOptions";
import { DISPLAY_ONLY_TIMING_FIELDS, GROUND_TIME_RECALC_FIELDS } from "./fieldImpactUtils";
import { normalizeAircraftIdentifier, normalizeAirportCode } from "./routeUtils";

// index.js/App.jsx do not initialize dayjs plugins yet; keep module-level UTC registration.
dayjs.extend(utc);

const GROUND_TIME_FIELDS = ["ground_time_minutes"];

function hasTimeValue(value) {
    return value !== undefined && value !== null && value !== "";
}

export function normalizeMinutes(value) {
    if (value === undefined || value === null || value === "") {
        return null;
    }

    const minutes = Number(value);
    return Number.isFinite(minutes) ? minutes : null;
}

export function normalizeTimeValue(value) {
    if (!value) {
        return null;
    }

    const parsed = dayjs.utc(value);
    return parsed.isValid() ? parsed.toISOString() : value;
}

export function getEffectiveDepartureTime(flight) {
    return hasTimeValue(flight?.etd) ? flight.etd : flight?.std ?? null;
}

export function getEffectiveArrivalTime(flight) {
    return hasTimeValue(flight?.eta) ? flight.eta : flight?.sta ?? null;
}

export function getEffectiveDepartureTimeSourceLabel(flight) {
    return hasTimeValue(flight?.etd) ? "ETD" : "STD";
}

export function getEffectiveArrivalTimeSourceLabel(flight) {
    return hasTimeValue(flight?.eta) ? "ETA" : "STA";
}

export function getEffectiveTimeSourceLabels(flight) {
    return {
        departure: getEffectiveDepartureTimeSourceLabel(flight),
        arrival: getEffectiveArrivalTimeSourceLabel(flight),
    };
}

export function getFlightTimeDisplayInfo(flight, { useEffectiveTime = false } = {}) {
    const departureTime = useEffectiveTime ? getEffectiveDepartureTime(flight) : flight?.std ?? null;
    const arrivalTime = useEffectiveTime ? getEffectiveArrivalTime(flight) : flight?.sta ?? null;

    return {
        departureTime,
        arrivalTime,
        departureLabel: useEffectiveTime ? "STD/ETD" : "STD",
        arrivalLabel: useEffectiveTime ? "STA/ETA" : "STA",
        departureSourceLabel: useEffectiveTime ? getEffectiveDepartureTimeSourceLabel(flight) : "STD",
        arrivalSourceLabel: useEffectiveTime ? getEffectiveArrivalTimeSourceLabel(flight) : "STA",
    };
}

export function valuesEqual(field, before, after) {
    if (field === "std" || field === "sta") {
        return normalizeTimeValue(before) === normalizeTimeValue(after);
    }
    if (field === "stc_type") {
        return normalizeStcTypeEmptyValue(before) === normalizeStcTypeEmptyValue(after);
    }
    if (before && after && typeof before === "object" && typeof after === "object") {
        return JSON.stringify(before) === JSON.stringify(after);
    }
    return (before ?? null) === (after ?? null);
}

export function getIsoDay(value) {
    const day = dayjs.utc(value).day();
    return day === 0 ? 7 : day;
}

export function formatMinutesAsHHMM(value) {
    const minutes = normalizeMinutes(value);
    if (minutes === null) {
        return "--";
    }

    const sign = minutes < 0 ? "-" : "";
    const absoluteMinutes = Math.abs(minutes);
    const hours = Math.floor(absoluteMinutes / 60);
    const mins = absoluteMinutes % 60;
    return `${sign}${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
}

export function calculateDateTimeDiffMinutes(startValue, endValue, { clampToZero = false } = {}) {
    if (!startValue || !endValue) {
        return null;
    }

    const start = dayjs.utc(startValue);
    const end = dayjs.utc(endValue);
    if (!start.isValid() || !end.isValid()) {
        return null;
    }

    const diff = end.diff(start, "minute");
    return clampToZero && diff < 0 ? 0 : diff;
}

export function getFlightBlockTimeMinutes(flight) {
    return normalizeMinutes(flight?.block_time_minutes)
        ?? normalizeMinutes(flight?.flight_route_block_time_minutes)
        ?? normalizeMinutes(flight?.route_snapshot?.block_time_minutes)
        ?? calculateDateTimeDiffMinutes(flight?.std, flight?.sta, { clampToZero: true });
}

export function getFlightMinBlockTimeMinutes(flight) {
    return normalizeMinutes(flight?.min_block_time_minutes)
        ?? normalizeMinutes(flight?.route_snapshot?.min_block_time_minutes);
}

function getAirportConstraintMinGroundTime(flight, airportConstraintsMap = {}) {
    const arrivalAirport = flight?.arrival_airport;
    const normalizedArrivalAirport = normalizeAirportCode(arrivalAirport);
    const constraint = normalizedArrivalAirport
        ? airportConstraintsMap?.[normalizedArrivalAirport] ?? airportConstraintsMap?.[arrivalAirport]
        : airportConstraintsMap?.[arrivalAirport];
    return normalizeMinutes(
        constraint?.turnaround_time
        ?? constraint?.min_ground_time_minutes
        ?? constraint?.minimum_ground_time_minutes,
    );
}

export function getFlightMinGroundTimeMinutes(flight, airportConstraintsMap = {}) {
    // 只认到达站在机场主数据里的明确配置（含 0 分钟）；机场没配就返回空，
    // 不要回到航班上存着的数字——那可能是旧版写下的假 1 小时
    return getAirportConstraintMinGroundTime(flight, airportConstraintsMap);
}

function getFlightKey(flight, index) {
    return String(flight?.id ?? flight?.flight_id ?? `flight-${index}`);
}

function getTimingGroupKey(flight) {
    const trackId = flight?.track_id === undefined || flight?.track_id === null ? "" : String(flight.track_id).trim();
    const aircraftReg = normalizeAircraftIdentifier(flight?.aircraft_reg);
    if (!trackId || !aircraftReg) {
        return null;
    }
    return `${trackId}::${aircraftReg}`;
}

function isContinuousGroundTurn(previousFlight, nextFlight) {
    const arrivalAirport = normalizeAirportCode(previousFlight?.arrival_airport);
    const departureAirport = normalizeAirportCode(nextFlight?.departure_airport);
    return Boolean(arrivalAirport && departureAirport && arrivalAirport === departureAirport);
}

export function withCalculatedFlightTimings(flights = [], airportConstraintsMap = {}) {
    if (!Array.isArray(flights) || !flights.length) {
        return flights;
    }

    const updatedFlightsByKey = new Map();
    const groups = new Map();

    flights.forEach((flight, index) => {
        const key = getFlightKey(flight, index);
        const updatedFlight = {
            ...flight,
            ground_time_minutes: null,
            min_ground_time_minutes: getFlightMinGroundTimeMinutes(flight, airportConstraintsMap),
        };
        updatedFlightsByKey.set(key, updatedFlight);

        const groupKey = getTimingGroupKey(flight);
        if (groupKey) {
            const groupFlights = groups.get(groupKey) || [];
            groupFlights.push({ flight, key });
            groups.set(groupKey, groupFlights);
        }
    });

    groups.forEach((groupFlights) => {
        for (let index = 0; index < groupFlights.length - 1; index += 1) {
            const previous = groupFlights[index];
            const next = groupFlights[index + 1];
            if (!isContinuousGroundTurn(previous.flight, next.flight)) {
                continue;
            }

            const groundTimeMinutes = calculateDateTimeDiffMinutes(previous.flight?.sta, next.flight?.std);
            if (groundTimeMinutes === null) {
                continue;
            }

            const updatedPrevious = updatedFlightsByKey.get(previous.key);
            if (updatedPrevious) {
                updatedPrevious.ground_time_minutes = groundTimeMinutes;
            }
        }
    });

    return flights.map((flight, index) => updatedFlightsByKey.get(getFlightKey(flight, index)) || flight);
}

function getChangeFlightId(change) {
    return change?.flightId ?? change?.flight_id;
}

function applyChangesToFlightsMap(flightsMap, changes = []) {
    const nextFlightsMap = new Map(flightsMap || []);

    changes.forEach((change) => {
        const flightId = getChangeFlightId(change);
        if (!flightId || !change?.field) {
            return;
        }

        const flightIdKey = String(flightId);
        const currentFlight = nextFlightsMap.get(flightIdKey);
        if (!currentFlight) {
            return;
        }

        nextFlightsMap.set(flightIdKey, {
            ...currentFlight,
            [change.field]: change.after,
        });
    });

    return nextFlightsMap;
}

function mergeChange(changes, nextChange) {
    const nextFlightId = getChangeFlightId(nextChange);
    if (!nextFlightId || !nextChange?.field) {
        return changes;
    }

    const existingIndex = changes.findIndex((change) => String(getChangeFlightId(change)) === String(nextFlightId) && change.field === nextChange.field);
    if (existingIndex < 0) {
        return [...changes, nextChange];
    }

    return changes.map((change, index) => (index === existingIndex ? { ...change, after: nextChange.after } : change));
}

function removeChange(changes, targetChange) {
    const targetFlightId = getChangeFlightId(targetChange);
    if (!targetFlightId || !targetChange?.field) {
        return changes;
    }

    return changes.filter((change) => !(String(getChangeFlightId(change)) === String(targetFlightId) && change.field === targetChange.field));
}

function timingValuesEqual(left, right) {
    return normalizeMinutes(left) === normalizeMinutes(right);
}

export function mergeCalculatedGroundTimeChanges(changes = [], flightsMap, airportConstraintsMap = {}) {
    const baseChanges = changes.filter((change) => !DISPLAY_ONLY_TIMING_FIELDS.has(change?.field));

    if (!flightsMap?.size) {
        return baseChanges;
    }

    const shouldRecalculate = baseChanges.some((change) => GROUND_TIME_RECALC_FIELDS.has(change?.field));
    if (!shouldRecalculate) {
        return baseChanges;
    }

    const nextFlightsMap = applyChangesToFlightsMap(flightsMap, baseChanges);
    const affectedGroupKeys = new Set();

    baseChanges.forEach((change) => {
        if (!GROUND_TIME_RECALC_FIELDS.has(change?.field)) {
            return;
        }

        const flightId = getChangeFlightId(change);
        if (!flightId) {
            return;
        }

        const beforeFlight = flightsMap.get(String(flightId));
        const afterFlight = nextFlightsMap.get(String(flightId));
        const beforeGroupKey = beforeFlight ? getTimingGroupKey(beforeFlight) : null;
        const afterGroupKey = afterFlight ? getTimingGroupKey(afterFlight) : null;
        if (beforeGroupKey) {
            affectedGroupKeys.add(beforeGroupKey);
        }
        if (afterGroupKey) {
            affectedGroupKeys.add(afterGroupKey);
        }
    });

    if (!affectedGroupKeys.size) {
        return baseChanges;
    }

    const affectedFlights = Array.from(nextFlightsMap.values()).filter((flight) => affectedGroupKeys.has(getTimingGroupKey(flight)));
    const recalculatedFlights = withCalculatedFlightTimings(affectedFlights, airportConstraintsMap);

    return recalculatedFlights.reduce((mergedChanges, afterFlight) => {
        const flightId = afterFlight?.id ?? afterFlight?.flight_id;
        if (!flightId) {
            return mergedChanges;
        }

        const beforeFlight = flightsMap.get(String(flightId));
        if (!beforeFlight) {
            return mergedChanges;
        }

        return GROUND_TIME_FIELDS.reduce((nextMergedChanges, field) => {
            const before = beforeFlight[field] ?? null;
            const after = afterFlight[field] ?? null;
            if (timingValuesEqual(before, after)) {
                return removeChange(nextMergedChanges, {
                    flightId: String(flightId),
                    field,
                });
            }

            return mergeChange(nextMergedChanges, {
                flightId: String(flightId),
                field,
                before,
                after,
            });
        }, mergedChanges);
    }, [...baseChanges]);
}
